// src/services/projectServiceNew.js
import { 
  createService, 
  createFileUploadConfig, 
  createNestedCollection
} from './factory';
import { objectToQueryString } from '../utils/apiUtils';
import { api } from './api';

// Create the base project service using our factory
const projectServiceBase = createService('Project', '/projects', {
  // File upload configuration for project documents
  fileUploadOptions: createFileUploadConfig('file', 'documents'),
  
  // Nested collections (one-to-many relationships)
  nestedCollections: [
    createNestedCollection('milestones', 'milestone', null, true),
    createNestedCollection('team', 'teamMember'),
    createNestedCollection('documents', 'document'),
    createNestedCollection('budget/transactions', 'transaction')
  ]
});

// ========================
// Public API (backward compatible with existing projectService)
// This ensures all existing code continues to work with the new implementation
// ========================

// Get projects with optional filters
export const getProjects = async (params = {}) => {
  return projectServiceBase.getAll(params);
};

// Get projects by client
export const getClientProjects = async (clientId, params = {}) => {
  return projectServiceBase.getFromParent(clientId, '/clients', params);
};

// Get a single project by ID
export const getProject = async (projectId) => {
  return projectServiceBase.getById(projectId);
};

// Create a new project
export const createProject = async (projectData) => {
  return projectServiceBase.create(projectData);
};

// Update an existing project
export const updateProject = async (projectId, projectData) => {
  return projectServiceBase.update(projectId, projectData);
};

// Delete a project
export const deleteProject = async (projectId) => {
  return projectServiceBase.delete(projectId);
};

// Update project status
export const updateProjectStatus = async (projectId, status) => {
  return projectServiceBase.updateStatus(projectId, status);
};

// Add a milestone to a project
export const addProjectMilestone = async (projectId, milestoneData) => {
  return projectServiceBase.addMilestone(projectId, milestoneData);
};

// Update a project milestone
export const updateProjectMilestone = async (projectId, milestoneId, milestoneData) => {
  return projectServiceBase.updateMilestone(projectId, milestoneId, milestoneData);
};

// Add a team member to a project
export const addProjectTeamMember = async (projectId, teamMemberData) => {
  return projectServiceBase.addTeamMember(projectId, teamMemberData);
};

// Update a project team member
export const updateProjectTeamMember = async (projectId, memberId, teamMemberData) => {
  return projectServiceBase.updateTeamMember(projectId, memberId, teamMemberData);
};

// Remove a team member from a project
export const removeProjectTeamMember = async (projectId, memberId) => {
  return projectServiceBase.removeTeamMember(projectId, memberId);
};

// Add a budget transaction
export const addBudgetTransaction = async (projectId, transactionData) => {
  return projectServiceBase.addTransaction(projectId, transactionData);
};

// Upload a project document
export const uploadProjectDocument = async (projectId, file, documentData = {}) => {
  return projectServiceBase.uploadFile(projectId, file, documentData);
};

// Delete a project document
export const deleteProjectDocument = async (projectId, documentId) => {
  return api.delete(`/projects/${projectId}/documents/${documentId}`);
};

// Update a project document
export const updateProjectDocument = async (projectId, documentId, documentData) => {
  return api.put(`/projects/${projectId}/documents/${documentId}`, documentData);
};

// Get projects by date range
export const getProjectsByDateRange = async (startDate, endDate) => {
  const queryParams = new URLSearchParams({
    startDate,
    endDate
  });
  
  return api.get(`/projects/date-range?${queryParams.toString()}`);
};

// Export the base service for direct usage if needed
export const projectService = projectServiceBase;